import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import MenuIcon from '@material-ui/icons/Menu'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import React, { Fragment, useState, useEffect, useCallback } from 'react'
import DocumentTitle from 'react-document-title'
import {
    useHistory,
    useLocation,
    useParams,
    useRouteMatch,
} from 'react-router-dom'
import 'react-table/react-table.css'
import {
    APP_BAR_HEIGHT,
    REFRESH_DELAY,
    DEFAULT_LOCATION,
    SECONDARY_HEADER_HEIGHT,
    CATALOG_HEADER_HEIGHT,
} from '../../../Constants'
import AssetNavigationHeader from './AssetNavigationHeader'
import EquipmentImageDetail from './EquipmentImageDetail'
import eventCapture from '../../../eventList'
import { posthogCapture } from '../../../PostHogFunction'
import withSelectedCategory from '../list/withSelectedCategory'
import { useItemStore } from '../../zustandState/ZustandStore'
import EvaluationDataProvider from '../dashboard/eval_components/EvaluationDataProvider'
import config from '../../../config/runtime'

const useStyles = makeStyles((theme) => ({
    root: { height: `calc(100% - ${APP_BAR_HEIGHT}px) !important` },
    rootNoAppBar: { height: `100% !important` },
    inner: {
        backgroundColor: theme.palette.environment.light.level0.base,
        flex: '1 1',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    menuButton: {
        color: theme.palette.button.standard.secondary,
        backgroundColor: 'white',
        '-webkit-filter': 'drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.2))',
        filter: 'drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.2))',
        '&:hover': { backgroundColor: 'white' },
    },
    progress: {
        position: 'fixed',
        top:
            APP_BAR_HEIGHT +
            SECONDARY_HEADER_HEIGHT +
            CATALOG_HEADER_HEIGHT +
            10,
        left: 10,
        zIndex: 1001,
    },
}))

function EquipmentDetailMain(props) {
    const {
        evalItem = {},
        onRefresh,
        isLoading: propsIsLoading,
        onDrawerOpen,
        onDrawerClose,
        isDrawerOpen,
        selectedCategory,
    } = props

    console.log({ selectedCategory, evalItem })

    const classes = useStyles()
    const history = useHistory()
    const location = useLocation()
    const match = useRouteMatch()
    const params = useParams()

    const itemStore = useItemStore.getState()

    const [isThumbnailsSelected, setIsThumbnailsSelected] = useState(false)
    const [evaluation, setEvaluation] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [tokenRefreshError, setTokenRefreshError] = useState(false)
    const [currentEvaluationId, setCurrentEvaluationId] = useState('')

    const { isZoomed, isEdit, isNew } = get(location, 'state', {})

    useEffect(() => {
        const evaluationId = evalItem?.PK?.split?.('|')[1]
        setCurrentEvaluationId(evaluationId)
    }, [evalItem])

    useEffect(() => {
        const handleKey = (event) => {
            if (!event.defaultPrevented) {
                if (event.key === 'Escape' && isDrawerOpen) {
                    event.preventDefault()
                    onDrawerClose()
                }
            }
        }

        document.addEventListener('keydown', handleKey, false)

        return () => {
            document.removeEventListener('keydown', handleKey, false)
        }
    }, [isDrawerOpen, onDrawerClose])

    useEffect(() => {
        if (!isNew && !get(params, 'id')) {
            history.push(DEFAULT_LOCATION)
        } else if (isZoomed) {
            history.push(match.url, { isZoomed: false, isEdit, isNew })
        }
    }, [history, isEdit, isNew, isZoomed, match.url, params])

    const back = () => {
        history.push(evaluation.uri)
    }

    const handleRefresh = useCallback(
        (item, isRefreshList, totalPhotos) => {
            if (totalPhotos) {
                setTimeout(() => {
                    const imageCount = get(evalItem, 'image_data.length', 0)
                    if (!imageCount || totalPhotos > imageCount) {
                        console.log(
                            `Refreshing to get last images. imageCount = ${imageCount} and totalPhotos = ${totalPhotos}`
                        )
                        onRefresh(item, isRefreshList, totalPhotos)
                    }
                }, REFRESH_DELAY)
            }
            return onRefresh(item, isRefreshList, totalPhotos)
        },
        [evalItem, onRefresh]
    )

    const getSelectedIndex = useCallback(() => {
        const sortedItemsList = itemStore.sortedItems
        const list =
            sortedItemsList && sortedItemsList.length > 0
                ? sortedItemsList
                : (evaluation && evaluation.items) || []

        if (list) {
            let selectedIndex =
                list.findIndex((item1) => match.url.indexOf(item1.uri) >= 0) ||
                0
            return selectedIndex < 0 ? 0 : selectedIndex
        }
        return 0
    }, [match.url, evaluation, itemStore.sortedItems])

    const onNext = () => {
        posthogCapture(eventCapture.NEXT_ITEM)
        const selectedIndex = getSelectedIndex()
        const sortedItemsList = itemStore.sortedItems
        const list =
            sortedItemsList && sortedItemsList.length > 0
                ? sortedItemsList
                : evaluation.items

        history.push(list[selectedIndex + 1].uri)
    }

    const onPrevious = () => {
        posthogCapture(eventCapture.PREVIOUS_ITEM)
        const selectedIndex = getSelectedIndex()
        const sortedItemsList = itemStore.sortedItems
        const list =
            sortedItemsList && sortedItemsList.length > 0
                ? sortedItemsList
                : evaluation.items

        history.push(list[selectedIndex - 1].uri)
    }

    const onZoom = (isZoomed) => {
        // STORE ZOOM STATUS IN LOCATION STATE
        history.push(match.url, { ...location.state, isZoomed })
    }

    const handleThumbnailsSelected = (isThumbnailsSelected) => {
        setIsThumbnailsSelected(isThumbnailsSelected)
    }

    const sortedItemsList = itemStore.sortedItems

    const total =
        sortedItemsList && sortedItemsList.length
            ? sortedItemsList.length
            : get(evaluation, 'item_count', 1)

    const selectedIndex = getSelectedIndex()
    const item = isNew ? {} : get(evalItem, 'item', {})

    let title = ''
    if (get(evaluation, 'evaluate', item.owner)) {
        title +=
            ' - ' + get(evaluation, 'evaluation.owner_name', item.owner) + ' '
    }
    title = (title || ' - ') + get(item, 'description', 'N/A')
    title = config.SITE_TITLE + title

    return (
        <DocumentTitle title={title}>
            <Fragment>
                <EvaluationDataProvider
                    evalID={currentEvaluationId}
                    onData={(data) => {
                        setEvaluation(data)
                    }}
                    onLoading={setIsLoading}
                    onError={setError}
                    onTokenRefreshError={setTokenRefreshError}
                />
                {(isLoading || propsIsLoading) && (
                    <CircularProgress className={classes.progress} />
                )}
                <Grid
                    container
                    className={classes.inner}
                    name={'EquipmentDetailMain-container'}
                    direction={'column'}
                    wrap={'nowrap'}
                    spacing={0}
                >
                    {!isThumbnailsSelected && (
                        <AssetNavigationHeader
                            zoomed={isZoomed}
                            onBack={back}
                            evaluation={evaluation}
                            selectedIndex={selectedIndex}
                            isNew={isNew}
                            onPrevious={onPrevious}
                            item={item}
                            total={total}
                            onNext={onNext}
                            isDrawerOpen={isDrawerOpen}
                            onDrawerOpen={onDrawerOpen}
                        />
                    )}
                    <EquipmentImageDetail
                        key={item && item.SK}
                        evalItem={!isNew && evalItem}
                        isError={!!error}
                        onZoom={onZoom}
                        evaluation={evaluation}
                        onBack={back}
                        onThumbnailsSelected={handleThumbnailsSelected}
                        onRefresh={handleRefresh}
                    >
                        {isZoomed && (
                            <IconButton
                                className={classes.menuButton}
                                aria-label="Open drawer"
                                onClick={onDrawerOpen}
                                style={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 72,
                                    zIndex: 2000,
                                    display: isDrawerOpen ? 'none' : undefined,
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                    </EquipmentImageDetail>
                </Grid>
            </Fragment>
        </DocumentTitle>
    )
}

EquipmentDetailMain.propTypes = {
    evalItem: PropTypes.object, // The equipment data.
    onRefresh: PropTypes.func.isRequired, // Callback when the photos have been added and needs a refresh.
    isLoading: PropTypes.bool,
    onDrawerOpen: PropTypes.func.isRequired,
    onDrawerClose: PropTypes.func.isRequired,
    isDrawerOpen: PropTypes.bool,
    selectedCategory: PropTypes.object,
}

EquipmentDetailMain.defaultProps = {
    evalItem: {},
    isLoading: false,
    isDrawerOpen: false,
}

export default withSelectedCategory(EquipmentDetailMain)
